import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Eye, Download, Trash2, FileText } from "lucide-react";
import {
  ConfirmDialog,
  Empty,
  Skeleton,
  StatusBadge,
  formatDate,
} from "../../dashboardAdmin/sidebar/adminLayout"; 

const PopupTasks = ({ userId, tasks: tasksProp, onTasksUpdate }) => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const controlled = Array.isArray(tasksProp);
  const [ownTasks, setOwnTasks] = useState([]);
  const [loading, setLoading] = useState(!controlled);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const tasks = controlled ? tasksProp : ownTasks;

  /* Mise à jour de la liste : état local (mode autonome) + notification du parent */
  const publish = (list) => {
    if (!controlled) setOwnTasks(list);
    if (onTasksUpdate) onTasksUpdate(list);
  };

  /* ---------- Chargement (mode autonome uniquement) ---------- */
  useEffect(() => {
    if (controlled) return;
    if (!userId) {
      setError("Missing user ID.");
      setLoading(false);
      return;
    }
    if (!localStorage.getItem("token")) {
      setError("No authentication token. Please log in again.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/tasks/user/${userId}`, {
          headers: authHeaders(),
        });
        const fetched = response.data.tasks || [];
        if (!cancelled) {
          setOwnTasks(fetched);
          if (onTasksUpdate) onTasksUpdate(fetched);
        }
      } catch (err) {
        console.error("Error fetching tasks:", err);
        if (!cancelled) {
          setError(err.response?.data?.message || "Failed to fetch tasks.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTasks();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, controlled]);

  /* ---------- Voir / télécharger / supprimer ---------- */
  const handleViewPDF = async (taskId) => {
    if (!localStorage.getItem("token")) {
      return toast.error("No authentication token. Please log in again.");
    }
    try {
      const response = await axios.get(`${API_URL}/tasks/view/${taskId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Error opening PDF:", err);
      toast.error(err.response?.data?.message || "Unable to open the PDF.");
    }
  };

  const handleDownloadPDF = async (taskId) => {
    if (!localStorage.getItem("token")) {
      return toast.error("No authentication token. Please log in again.");
    }
    try {
      const response = await axios.get(`${API_URL}/tasks/download/${taskId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `task-${taskId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      toast.error(err.response?.data?.message || "Unable to download the PDF.");
    }
  };

  const deleteTask = async (taskId) => {
    if (!localStorage.getItem("token")) {
      return toast.error("No authentication token. Please log in again.");
    }
    try {
      await axios.delete(`${API_URL}/tasks/${taskId}`, { headers: authHeaders() });
      publish(tasks.filter((t) => t._id !== taskId));
      toast.success("Task deleted successfully!");
    } catch (err) {
      console.error("Error deleting task:", err);
      toast.error(err.response?.data?.message || "Unable to delete the task.");
    }
  };

  const askDelete = (taskId) =>
    setConfirm({
      title: "Delete this task?",
      message: "The task will be permanently deleted.",
      confirmLabel: "Delete",
      onConfirm: () => deleteTask(taskId),
    });

  const runConfirm = async () => {
    const current = confirm;
    setConfirm(null);
    if (current?.onConfirm) await current.onConfirm();
  };

  /* ---------- Rendu ---------- */
  if (loading) return <Skeleton rows={3} />;

  if (error) return <Empty>{error}</Empty>;

  /* Plus récentes d'abord */
  const sorted = [...tasks].sort((a, b) => new Date(b.taskDate) - new Date(a.taskDate));

  return (
    <>
      {sorted.length === 0 ? (
        <Empty>No tasks available for this user.</Empty>
      ) : (
        sorted.map((task) => (
          <div
            key={task._id}
            className="adm-doc"
            style={{ cursor: "pointer" }}
            onClick={() => handleViewPDF(task._id)}
          >
            <div className="adm-doc-icon">
              <FileText size={18} />
            </div>
            <div className="adm-doc-text">
              <div className="adm-doc-title">Task · {formatDate(task.taskDate)}</div>
              {task.status && (
                <div className="adm-doc-date">
                  <StatusBadge status={task.status} />
                </div>
              )}
            </div>
            <button
              className="adm-icon-btn"
              title="View PDF"
              onClick={(e) => {
                e.stopPropagation();
                handleViewPDF(task._id);
              }}
            >
              <Eye />
            </button>
            <button
              className="adm-icon-btn"
              title="Download PDF"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadPDF(task._id);
              }}
            >
              <Download />
            </button>
            <button
              className="adm-icon-btn danger"
              title="Delete task"
              onClick={(e) => {
                e.stopPropagation();
                askDelete(task._id);
              }}
            >
              <Trash2 />
            </button>
          </div>
        ))
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
};

export default PopupTasks;